import { useEffect, useState } from 'react';
import {
  localStorageColorSchemeManager,
  MantineProvider,
  useMantineColorScheme,
} from '@mantine/core';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { theme, cssVariablesResolver, READING_SURFACE_CSS } from '../theme/theme';
import { api, ApiError } from '../lib/api';
import { purgeUserCaches, shouldPurgeForLearner } from '../lib/sw';
import { applyThemeColor, type SchemeChoice } from '../lib/theme-color';
import { MarkLoop, MARK_CSS } from '../components/brand/Mark';
import { Shell } from './Shell';
import { StudyProvider } from './study';
import { SignIn, SIGN_IN_CSS } from '../screens/SignIn';
import { Landing } from '../screens/Landing';
import { Today } from '../screens/Today';
import { Library } from '../screens/Library';
import { Watch } from '../screens/Watch';
import { AddVideo } from '../screens/AddVideo';
import { Words } from '../screens/Words';
import { ItemDetail, LessonScreen } from '../screens/ItemDetail';
import { Coverage } from '../screens/Coverage';
import { Me } from '../screens/Me';
import { Placeholder } from '../screens/Placeholder';
import { Practice } from '../screens/Practice';
import { Cards } from '../screens/Cards';
import { Placement } from '../screens/Placement';
import { Review } from '../screens/Review';
import { Read, ReadStory, READER_CSS } from '../screens/Read';
import { Listen } from '../screens/Listen';
import { Journal, JournalEntry, JOURNAL_CSS } from '../screens/Journal';
import { Chat, ChatRun, CHAT_CSS } from '../screens/Chat';

/** The YouTube iframe fills its 16:9 box; the app never draws over it (F4 principle 8). */
const PLAYER_CSS = `
.nn-player iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
`;

/**
 * `networkMode: 'offlineFirst'` is what makes the installed app work when the network goes away
 * while it is already open. React Query's default `'online'` PAUSES every new query the moment
 * the window sees an `offline` event, so navigating to Words in airplane mode would show
 * skeletons forever instead of asking the service worker, which has the answer. A cold open
 * works either way; the resumed case is the one Android actually produces.
 */
const qc = new QueryClient({
  defaultOptions: { queries: { retry: false, staleTime: 30_000, networkMode: 'offlineFirst' } },
});

/** Device-scoped by nature — unlike the display settings, which follow the learner's profile. */
const colorSchemeManager = localStorageColorSchemeManager({ key: 'nn.scheme' });

export function App() {
  return (
    <MantineProvider
      theme={theme}
      cssVariablesResolver={cssVariablesResolver}
      colorSchemeManager={colorSchemeManager}
      defaultColorScheme="dark"
    >
      {/* One <style> for every screen-owned rule sheet. `JOURNAL_CSS` and `CHAT_CSS` join it for
          the rules that cannot be inline styles — `::placeholder` colours and the chip strips'
          hidden scrollbars. Neither underline is in here: both are inline on the span they mark,
          so a sheet that failed to be concatenated could never make either feature look like it
          simply found no mistakes. `CHAT_CSS` also carries the composer's own box, which is
          class-based because it is a `<textarea>` that grows — losing it would leave the chat's
          input styled by the browser rather than unstyled-but-obvious. */}
      <style>
        {READING_SURFACE_CSS +
          PLAYER_CSS +
          MARK_CSS +
          SIGN_IN_CSS +
          READER_CSS +
          JOURNAL_CSS +
          CHAT_CSS}
      </style>
      <ThemeColorSync />
      <QueryClientProvider client={qc}>
        <BrowserRouter>
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route element={<Authed />}>
              <Route path="/" element={<Today />} />
              <Route path="/watch" element={<Library />} />
              <Route path="/watch/:videoId" element={<WatchRoute />} />
              <Route path="/practice" element={<Practice />} />
              {/* Remounted per run: the scope is read once at mount, so navigating from one
                  scoped review to another (a vocabulary preview, then the bite's card) has to
                  build a new run rather than reuse the old one's state. */}
              <Route path="/review" element={<ReviewRoute />} />
              {/* M10-iv: the card session. Its own route, not a mode of /review — the deck is the
                  due list but the interaction, the evidence channel and the summary are all different. */}
              <Route path="/cards" element={<Cards />} />
              {/* M10-iii, α: the wizard is a full-screen FLOW. It sits inside <Authed>
                  for the session, but it renders its own frame and no nav — chrome would
                  invite wandering mid-run. */}
              <Route path="/placement" element={<Placement />} />
              <Route path="/read" element={<Read />} />
              {/* Keyed on the story so the dwell ledger, the marks and the end checks never
                  carry over from the story before it. */}
              <Route path="/read/:storyId" element={<ReadRoute />} />
              <Route path="/listen" element={<ListenRoute />} />
              <Route path="/journal" element={<Journal />} />
              {/* Keyed on the composition, and this one is load-bearing rather than tidy: every
                  piece of editor state — the seeded draft, the re-anchored corrections, the map
                  of what has been acted on, the per-visit check counter — is built once at mount
                  from the entry it belongs to. Without the key, opening a second entry from the
                  list would show the first one's underlines over the second one's Georgian. */}
              <Route path="/journal/:compositionId" element={<JournalRoute />} />
              {/* Text chat is `/chat`. `/dialogue` is deliberately still unrouted and still
                  swallowed by the catch-all below: Practice has linked there since M6a and M6d
                  builds the spoken screen behind it, so claiming the path here would take a name
                  this feature is not. */}
              <Route path="/chat" element={<Chat />} />
              {/* Keyed on the conversation, for the reason the journal's route states: the turns,
                  the pending bubble, the retry ULID, the acted-on map and the recap are all run
                  state built once at mount. Without the key, opening a second conversation from
                  the history would show the first one's transcript under the second one's id. */}
              <Route path="/chat/:conversationId" element={<ChatRoute />} />
              <Route path="/words" element={<Words />} />
              <Route path="/words/item/:itemId" element={<ItemDetail />} />
              <Route path="/words/lesson/:lessonId" element={<LessonScreen />} />
              <Route path="/me" element={<Me />} />
              <Route path="/me/coverage" element={<Coverage />} />
              <Route path="/add" element={<AddVideo />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </MantineProvider>
  );
}

/**
 * Keeps the browser chrome in step with a forced colour scheme. The two `theme-color` tags in
 * index.html are media-qualified, so on System they are already right and this is a no-op; when
 * a scheme is forced, the tag the OS is honouring has to be overwritten or the status bar stays
 * the wrong colour under a correctly-themed app.
 */
function ThemeColorSync() {
  const { colorScheme } = useMantineColorScheme();
  useEffect(() => {
    applyThemeColor(colorScheme as SchemeChoice);
  }, [colorScheme]);
  return null;
}

/** Remount Watch per video so sheet/selection/EN state never carries over between videos. */
function WatchRoute() {
  const { videoId } = useParams();
  return <Watch key={videoId} />;
}

/** Remount Review per distinct scope, for the same reason: a run is built once, at mount. */
function ReviewRoute() {
  const { search } = useLocation();
  return <Review key={search} />;
}

/** Remount the reader per story: the dwell ledger and the end checks are per-story state. */
function ReadRoute() {
  const { storyId } = useParams();
  return <ReadStory key={storyId} />;
}

/** Remount the editor per entry: the draft, the corrections and the check counter are per-entry
 *  state, read once at mount — see the route's own comment. */
function JournalRoute() {
  const { compositionId } = useParams();
  return <JournalEntry key={compositionId} />;
}

/** Remount the run per conversation: the transcript and every piece of turn state are read once
 *  at mount — see the route's own comment. */
function ChatRoute() {
  const { conversationId } = useParams();
  return <ChatRun key={conversationId} />;
}

/** Remount Listen per span: the queue is built once, from the span the URL names. */
function ListenRoute() {
  const { search } = useLocation();
  return <Listen key={search} />;
}

/** How long a wait has to last before it is worth showing anything at all. */
const SPLASH_AFTER_MS = 600;

/**
 * Session gate: `/api/me` is session-required.
 *
 * A 401 at `/` is a visitor, not an error — they get the Landing page. A 401 anywhere else is
 * someone who followed a link into the app, and they get the door.
 */
function Authed() {
  const { pathname } = useLocation();
  const q = useQuery({ queryKey: ['me'], queryFn: api.me });
  const slow = useDelayed(SPLASH_AFTER_MS);
  const learnerId = q.data?.learner.id;

  /**
   * A different learner signing in on this device must not inherit the last one's cached reads
   * or audio. Sign-out purges too (Me), but this catches the case sign-out cannot: a session
   * that ended somewhere else, or a shared device where nobody pressed anything.
   */
  useEffect(() => {
    if (!learnerId) return;
    const prev = localStorage.getItem('nn.learner');
    if (shouldPurgeForLearner(prev, learnerId)) void purgeUserCaches();
    localStorage.setItem('nn.learner', learnerId);
  }, [learnerId]);

  if (q.isPending)
    // Nothing at all for the first paint — a splash that flashes for 200ms is worse than a
    // blank. Past that it is a real wait, and the brand loop is what fills it.
    return slow ? <BootSplash /> : null;
  if (q.error instanceof ApiError && q.error.problem.status === 401)
    return pathname === '/' ? <Landing /> : <Navigate to="/signin" replace />;
  if (q.error) return <Placeholder title="Something's off" body={String(q.error)} />;
  return (
    <StudyProvider me={q.data}>
      <Shell me={q.data} />
    </StudyProvider>
  );
}

function BootSplash() {
  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--nn-bg)',
      }}
    >
      <MarkLoop size={64} />
    </div>
  );
}

/** True once `ms` has passed since mount. */
function useDelayed(ms: number): boolean {
  const [passed, setPassed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setPassed(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return passed;
}
